import { ImgWH, LabelColor, ParagraphColor, Spacing, SpacingDTM, SpacingR } from '@pages/CommonStyle/common.style';
import { DivAllCenter, DivFlexColumn, DivFlexRow, DivPadding, DivWidth100 } from '@pages/CommonStyle/commonDiv.style';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  BanenrShowMore,
  BannerImageSection,
  BannerLayout,
  BannerProductButton,
  BannerProductCard,
  BannerProductCardTrack,
  BannerProductCardViewport,
  BannerSection,
  Dot,
  DotContainer,
} from './Banner.style';
import { BannerLogo, BigBannerImg } from '@assets/images/main/home';
import { DisplayH1fff, DisplayH2, DisplayH2color } from '@components/Text/HeadingTagDisplay.style';
import { StyledGridContainer } from '@components/Grid/Grid.style';
import { IconArrowLeftWhite, IconArrowRightWhite, IconChevRight, IconChevronRight } from '@assets/images/icon';
import { CommonState } from '@redux/reducers/commonReducer';
import { useSelector } from 'react-redux';
import home from '@src/lang/ko/main/home/home';
import i18n from '@pages/i18n';
import useDraggable from '@hooks/useDraggble';

const Banner = ({ onScrollButton }) => {
  const layoutType = useSelector((state: CommonState) => state.gridLayoutType);
  const cards = [0, 1, 2];
  const selectedIndexRef = useRef(0);
  const [selectedIndex, setSelectedIndexState] = useState(0); // :흰색_확인_표시: 하나만
  const setSelectedIndex = (value: number | ((prev: number) => number)) => {
    const newIndex = typeof value === 'function' ? value(selectedIndexRef.current) : value;

    selectedIndexRef.current = newIndex;
    // console.log('update!! ', newIndex, ' ', selectedIndexRef.current);
    setSelectedIndexState(newIndex);
  };
  const [offset, setOffset] = useState(0);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  /* 선택된 카드가 중앙에 오도록 트랙 위치 계산 */
  const recalcOffset = () => {
    const wrap = viewportRef.current;
    const card = wrap?.querySelector<HTMLDivElement>('.card');
    if (!wrap || !card) return;
    const centerSpace = (wrap.offsetWidth - card.offsetWidth) / 2;
    const newOffset = centerSpace - selectedIndexRef.current * (card.offsetWidth + 32);
    setOffset(newOffset);
  };
  useLayoutEffect(recalcOffset, [selectedIndex]);
  useLayoutEffect(() => {
    window.addEventListener('resize', recalcOffset);
    return () => window.removeEventListener('resize', recalcOffset);
  }, []);
  /* 좌우 이동 버튼 */
  const goPrev = () => setSelectedIndex((prev) => Math.max(0, prev - 1));
  const goNext = () => setSelectedIndex((prev) => Math.min(cards.length - 1, prev + 1));
  /* 카드 클릭 핸들러 */
  const handleCardClick = (idx: number) => {
    if (didDrag.current) return;
    setSelectedIndex(idx);
  };
  // console.log('current lang ', i18n.language);
  const { handleMouseDown, handleTouchStart, didDrag } = useDraggable({
    selectedIndex,
    itemsCount: cards.length,
    containerRef: viewportRef,
    offset,
    setOffset,
    setSelectedIndex,
    recalcOffset,
  });
  return (
    <>
      <BannerLayout>
        <BannerImageSection img={BannerLogo} />
        <BannerSection>
          <Spacing pixel="40" />
          <div style={{ zIndex: 1, width: '100%' }}>
            <StyledGridContainer>
              <SpacingDTM d={'192'} t="128" m="96" />
              <DisplayH1fff size={'medium'}>
                <Trans i18nKey={`home.titleBanner.title`} />
              </DisplayH1fff>
              <Spacing pixel="16" />
              <ParagraphColor color="#FFF" size="medium">
                <p>
                  <Trans i18nKey={`home.titleBanner.description`} />
                </p>
              </ParagraphColor>
              <SpacingDTM d={'64'} t="64" m="48" />
              <DivFlexRow>
                <BannerProductButton onClick={goPrev}>
                  <ImgWH w="24" h="24" src={IconArrowLeftWhite} />
                </BannerProductButton>
                <SpacingR pixel="8" />
                <BannerProductButton onClick={goNext}>
                  <ImgWH w="24" h="24" src={IconArrowRightWhite} />
                </BannerProductButton>
              </DivFlexRow>
              <SpacingDTM d={'64'} t="64" m="48" />
            </StyledGridContainer>
          </div>
          {/* 제품 카드 */}
          <BannerProductCardViewport ref={viewportRef}>
            <BannerProductCardTrack $offset={offset} onMouseDown={handleMouseDown} onTouchStart={handleTouchStart}>
              {home.banner.productCard.map((product, index) => (
                <BannerProductCard
                  key={product.title}
                  className="card"
                  bgImg={product.bgImg[layoutType]}
                  $offset={offset}
                  onClick={() => handleCardClick(index)} // 데모용
                >
                  <DivFlexColumn>
                    <DisplayH2color size={'small'} color={'#fff'}>
                      <Trans i18nKey={`home.banner.productCard.${index}.title`} />
                    </DisplayH2color>
                    <Spacing pixel="8" />
                    <ParagraphColor color={'#fff'} size={'small'}>
                      <Trans i18nKey={`home.banner.productCard.${index}.desc`} />
                    </ParagraphColor>
                    <Spacing pixel="16" />
                    <BanenrShowMore href={i18n.language === 'ko' ? `${product.link}` : `/${i18n.language}${product.link}`} rel="noopener noreferrer">
                      <LabelColor size="medium" color="#fff">
                        <Trans i18nKey={`home.showMore`} />{' '}
                      </LabelColor>
                      <DivPadding a={'0'} b={'0'} c={'0'} d={'4'} />
                      <ImgWH src={IconChevRight} alt="Right Arrow Icon" w={'20'} h={'20'} />
                    </BanenrShowMore>
                  </DivFlexColumn>
                </BannerProductCard>
              ))}
            </BannerProductCardTrack>
          </BannerProductCardViewport>
          <Spacing pixel="32" />
          <div style={{ zIndex: 1, width: '100%' }}>
            <StyledGridContainer>
              {/* ---------- 도트 인디케이터 ---------- */}
              <DotContainer>
                {cards.map((_, i) => (
                  <Dot key={i} $active={i === selectedIndex} />
                ))}
              </DotContainer>
            </StyledGridContainer>
          </div>
          <Spacing pixel="64" />
          <SpacingDTM d="32" t="0" m="0" />
        </BannerSection>
      </BannerLayout>
    </>
  );
};
export default Banner;
